import { useTranslation } from "react-i18next";
import {
  addDays,
  avatarColorForId,
  fmtNoShort,
  formatAppointmentTime,
  initials,
} from "./schedule-utils";

const STEP_LABEL_KEYS = {
  employee: "report.agreement.redesign.step_employee",
  schedule: "report.agreement.redesign.step_day_time",
  settings: "report.agreement.redesign.step_settings",
  edit: "report.agreement.redesign.step_schedule",
  notify: "report.agreement.redesign.step_notify",
};

export default function ReportAgreementModalFoot({
  appointment,
  employee,
  weekStart,
  hasConflict,
  step,
  fixedTime,
  onBack,
  onNext,
  onClose,
  onSubmit,
  isSubmitting,
  nextDisabled = false,
  nextLabel,
  showSummary = true,
}) {
  const { t } = useTranslation();
  const dateLabel = appointment ? fmtNoShort(addDays(weekStart, appointment.day)) : null;
  const timeLabel = appointment ? formatAppointmentTime(appointment, fixedTime) : null;

  if (step === "notify") {
    return (
      <div className="ra-modal-foot">
        <div className="ra-foot-summary">
          <button type="button" className="ra-btn ghost" onClick={onBack}>
            ← {t("Back")}
          </button>
        </div>
        <div className="ra-foot-actions">
          <button
            type="button"
            className="ra-btn ghost"
            onClick={() => onSubmit({ notify: false })}
            disabled={isSubmitting}
          >
            {t("report.agreement.redesign.skip_notify")}
          </button>
          <button
            type="button"
            className="ra-btn primary"
            onClick={() => onSubmit({ notify: true })}
            disabled={isSubmitting}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M2.5 8l3.5 3.5L13.5 4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {t("report.agreement.redesign.send_agreement")}
          </button>
        </div>
      </div>
    );
  }

  const canGoNext = !nextDisabled;

  return (
    <div className="ra-modal-foot">
      {showSummary ? (
        <div className="ra-foot-summary">
          {employee && (
            <span className="ra-pill">
              <span
                className="ra-emp-avatar"
                style={{
                  background: avatarColorForId(employee.id),
                  width: 18,
                  height: 18,
                  fontSize: 9,
                }}
              >
                {initials(employee.displayTitle || employee.name)}
              </span>
              {employee.displayTitle || employee.name}
            </span>
          )}
          {appointment ? (
            <span className={`ra-pill ${hasConflict ? "warn" : "ok"}`}>
              {hasConflict ? "⚠" : "✓"} {dateLabel} · {timeLabel}
            </span>
          ) : step === "edit" || step === "schedule" ? (
            <span className="ra-pill" style={{ color: "var(--text-faint)" }}>
              {t("report.agreement.redesign.pick_time_hint")}
            </span>
          ) : null}
          {hasConflict && (
            <span style={{ fontSize: 12, color: "#92400e" }}>
              {t("report.agreement.redesign.conflict_hint")}
            </span>
          )}
        </div>
      ) : (
        <div className="ra-foot-summary">
          {onBack ? (
            <button type="button" className="ra-btn ghost" onClick={onBack}>
              ← {t("Back")}
            </button>
          ) : (
            <button type="button" className="ra-btn ghost" onClick={onClose}>
              {t("Close")}
            </button>
          )}
        </div>
      )}
      <div className="ra-foot-actions">
        {showSummary && (
          <button type="button" className="ra-btn" onClick={onBack || onClose}>
            {onBack ? t("Back") : t("Close")}
          </button>
        )}
        <button
          type="button"
          className="ra-btn primary"
          onClick={onNext}
          disabled={!canGoNext}
        >
          {nextLabel || t("report.agreement.redesign.next_notify")}
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M5 3l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
    </div>
  );
}

export function ReportAgreementModalHead({
  step,
  onClose,
  t,
  wizardSteps = null,
}) {
  const steps =
    wizardSteps ||
    [
      { id: "edit", labelKey: STEP_LABEL_KEYS.edit },
      { id: "notify", labelKey: STEP_LABEL_KEYS.notify },
    ];
  const activeIdx = Math.max(
    0,
    steps.findIndex((s) => s.id === step)
  );
  const activeLabel = t(
    steps[activeIdx]?.labelKey || STEP_LABEL_KEYS[step] || STEP_LABEL_KEYS.edit
  );

  return (
    <div className="ra-modal-head">
      <div>
        <h2>{t("report.agreement.new")}</h2>
        <p>{t("report.agreement.modal_intro")}</p>
      </div>
      <div className="ra-modal-head-right">
        <div className="ra-steps" aria-label={activeLabel}>
          {steps.map((s, idx) => (
            <span key={s.id} className="ra-step-item">
              {idx > 0 && <div className="ra-step-line" />}
              <div
                className={`ra-step-dot ${
                  idx === activeIdx ? "active" : idx < activeIdx ? "done" : ""
                }`}
              >
                {idx + 1}
              </div>
            </span>
          ))}
          <span className="ra-step-label">{activeLabel}</span>
        </div>
        <button type="button" className="ra-close-x" onClick={onClose} aria-label={t("Close")}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </button>
      </div>
    </div>
  );
}
